import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthInitializationRecovery } from "@/components/AuthInitializationRecovery";

/**
 * Gates a route on an authenticated session. While the initial session
 * restore is in flight we render a tiny loading shell rather than
 * redirecting — otherwise a logged-in user briefly bounces to /login on
 * cold load before the cookie hydrates.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, isLoading, initializationError } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (initializationError) {
    return <AuthInitializationRecovery />;
  }

  if (!session) {
    // Preserve the attempted path so /login can return the user here after
    // sign-in (state.from picked up by Login).
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
